import React, { useEffect, useState } from 'react';
import { Select, Option, Button, Input, Flex } from '@pega/cosmos-react-core';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { appState } from '../../../../interfaces/common';
import { useNavigate } from 'react-router-dom';
import { updateWidgetButton } from '../ChatApis';

type WidgetFormData = {
  widgetButtonText: string;
};

const WidgetButtonConfig: React.FC = () => {
  const [showInput, updateShowInput] = useState(false);
  const [widgetStyle, setWidgetStyle] = useState('');
  const [textOrientation, setTextOrientation] = useState('horizontal');
  const { register, handleSubmit, errors, reset, formState } = useForm<WidgetFormData>({ mode: 'all' });
  const translate = useLocalTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    chatInfo: { connectionId, widgetButtonType, widgetButtonText, textOrientationType },
    sessionId,
  } = useSelector((state: appState) => {
    return {
      chatInfo: state.chatInfo,
      sessionId: state.sessionId,
    };
  });

  useEffect(() => {
    if (widgetButtonType) setWidgetStyle(widgetButtonType);
    if (widgetButtonType && widgetButtonType === 'text') {
      updateShowInput(true);
    } else {
      updateShowInput(false);
    }

    if (textOrientationType) setTextOrientation(textOrientationType);

    const formValues = {
      widgetButtonText,
    };
    reset(formValues);
  }, [reset, widgetButtonType, widgetButtonText, textOrientationType]);

  const updateWidgetButtonType = async (e: any) => {
    if (e === 'text') {
      updateShowInput(true);
    } else {
      updateShowInput(false);
      const payload = {
        widgetButtonType: e,
      };
      updateButtonType(payload);
    }
    setWidgetStyle(e);
  };

  const updateTextOrientation = (e: any) => {
    setTextOrientation(e);
  };

  const onSubmit = ({ widgetButtonText }: WidgetFormData) => {
    const payload = {
      widgetButtonType: 'text',
      widgetButtonText: widgetButtonText,
      textOrientationType: textOrientation,
    };
    updateButtonType(payload);
  };

  const updateButtonType = async (payload: any) => {
    dispatch(updateLoaderDetails(true));
    let json = await updateWidgetButton(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate('widget_button_type_update_success'), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('widget_button_type_update_failed'), 'urgent'));
    }
  };

  const maxLength = (inputVal: any) => {
    if (inputVal.length > 15) return true;
    return false;
  };

  return (
    <Flex
      container={{
        direction: 'row',
        alignItems: 'start',
        itemGap: 1,
      }}
      style={{ width: '120%' }}
    >
      <Flex item={{ grow: 0.41 }}>
        <Select
          label={translate('widget_button_type')}
          value={widgetStyle}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            updateWidgetButtonType(e.target.value);
          }}
        >
          <Option value="icon">{translate('icon')}</Option>
          <Option value="text">{translate('text')}</Option>
        </Select>
      </Flex>
      {showInput && (
        <Flex item={{ grow: 0.41 }}>
          <Select
            label={translate('text_orientation')}
            value={textOrientation}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              updateTextOrientation(e.target.value);
            }}
          >
            <Option value="horizontal">{translate('horizontal')}</Option>
            <Option value="vertical">{translate('vertical')}</Option>
          </Select>
        </Flex>
      )}
      {showInput && (
        <Input
          name="widgetButtonText"
          type="text"
          label={translate('button_text')}
          autoComplete="off"
          required={true}
          info={errors.widgetButtonText?.message}
          status={errors.widgetButtonText?.message ? 'error' : undefined}
          ref={register({
            required: translate('button_text_empty'),
            validate: {
              maxLength: (v) => maxLength(v) === false || translate('widget_button_max_length'),
            },
          })}
        />
      )}
      {showInput && (
        <Flex container style={{ paddingTop: '20px' }}>
          <Button type="submit" onClick={handleSubmit(onSubmit)} disabled={!formState.isValid}>
            {translate('save')}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default WidgetButtonConfig;
